
import React, { useState, useEffect } from "react";
import Modal from "./common/Modal";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

// Draft.js imports
import { EditorState, ContentState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "../css/email-editor.css";
import { apiRequest } from "../../../../utils/utils";

/**
 * Convert an HTML string into a Draft.js EditorState.
 * Falls back to an empty EditorState if the html is blank.
 */
const htmlToEditorState = (html) => {
  if (!html) return EditorState.createEmpty();
  const blocksFromHtml = htmlToDraft(html);
  if (blocksFromHtml) {
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap,
    );
    return EditorState.createWithContent(contentState);
  }
  return EditorState.createEmpty();
};

const SendEmailToLeadModal = ({
  contactId,
  showModal,
  closeModal,
  currentUser,
}) => {
  const mautic_url =
    process.env.REACT_APP_CRM_ADMIN ;

  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [errors, setErrors] = useState({});
  const [showFromFields, setShowFromFields] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [formData, setFormData] = useState({
    emailSubject: "",
    emailBody: "",
    emailTemplate: "",
    fromName: "",
    fromEmail: "",
  });

  // Load initial templates when modal opens
  useEffect(() => {
    if (showModal) {
      loadTemplateOptions("");
      setSelectedTemplate(null);
      setErrors({});
      setEditorState(EditorState.createEmpty());
      setFormData({
        emailSubject: "",
        emailBody: "",
        emailTemplate: "",
        fromName: currentUser.name || "",
        fromEmail: currentUser.email || "",
      });
    }
  }, [showModal]);

  useEffect(() => {
    if (currentUser) {
      setFormData((prevData) => ({
        ...prevData,
        fromName: currentUser.name || "",
        fromEmail: currentUser.email || "",
      }));
    }
  }, [currentUser]);

  const fetchEmailTemplates = async (search = "") => {
    try {
      const url = new URL(`${mautic_url}/api/crm/emails`);

      const params = {
        contact_id: contactId,
      };
      if (search) {
        params.search = search;
      }

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        toast.error("Failed to fetch Email Templates");
        return [];
      }

      const data = await response.json();
      const { emails } = data;

      return emails || [];
    } catch (error) {
      toast.error("Error fetching email templates: " + error.message);
      return [];
    }
  };

  const loadTemplateOptions = async (inputValue) => {
    const templates = await fetchEmailTemplates(inputValue);
    return templates.map((item) => ({
      label: item.name,
      value: item.id,
      template: item, // Include the entire template object
    }));
  };

  // Debounced version of loadTemplateOptions
  const debouncedLoadOptions = (inputValue, callback) => {
    if (window.debounceTimer) {
      clearTimeout(window.debounceTimer);
    }

    window.debounceTimer = setTimeout(async () => {
      const options = await loadTemplateOptions(inputValue);
      callback(options);
    }, 500);
  };

  const handleTemplateChange = (selectedOption) => {
    setSelectedTemplate(selectedOption);
    if (selectedOption) {
      // Access the template directly from the selectedOption
      const template = selectedOption.template;

      const htmlBody = template ? template.custom_html || "" : "";

      // Convert HTML to Draft.js EditorState
      const newEditorState = htmlToEditorState(htmlBody);
      setEditorState(newEditorState);

      setFormData({
        ...formData,
        emailTemplate: selectedOption.value,
        emailBody: htmlBody,
        emailSubject: template ? template.subject || "" : "",
      });
    } else {
      setEditorState(EditorState.createEmpty());
      setFormData({
        ...formData,
        emailTemplate: "",
        emailBody: "",
        emailSubject: "",
      });
    }
  };


  const handleEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
    const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    handleInputChange("emailBody", html);
  };

  const handleSendEmail = async () => {
    const isValid = validateForm();
    if (!isValid) return;

    setSaving(true);

    try {
      const muvi_useruuid = Cookies.get("muvi_useruuid");

      const response = await apiRequest(`${mautic_url}/api/crm/emails/send`,
        "POST",
        {
          contact_id: contactId,
          from_name: formData.fromName,
          from_email: formData.fromEmail,
          subject: formData.emailSubject,
          custom_html: formData.emailBody,
          muvi_useruuid: muvi_useruuid || null,
        },
        {},
        true
      );

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      toast.success("Email sent successfully");
      closeModal();
    } catch (error) {
      toast.error("Error sending email: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const validateForm = () => {
    setErrors({});
    let isValid = true;

    if (!formData.fromName.trim()) {
      setErrors((prev) => ({
        ...prev,
        fromName: "From Name is required",
      }));
      isValid = false;
    }

    if (!formData.fromEmail.trim()) {
      setErrors((prev) => ({ ...prev, fromEmail: "From Email is required" }));
      isValid = false;
    }
    return isValid;
  };

  const handleInputChange = (fieldName, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const footer = () => {
    return (
      <React.Fragment>
        <button
          type="button"
          className="cancel-btn"
          onClick={closeModal}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="save-btn"
          onClick={handleSendEmail}
          disabled={saving}
        >
          {saving ? "Sending..." : "Send"}
        </button>
      </React.Fragment>
    );
  };

  return (
    <div id="edit">
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={"Send Email to Lead"}
        className="crm"
        footer={footer()}
        scrollable={true}
      >
        <div className="input-box">
          <label className="input-label" htmlFor="emailTemplate">
            Email Template
          </label>
          <AsyncSelect
            value={selectedTemplate}
            className="single-select"
            classNamePrefix="single-select"
            closeMenuOnSelect={true}
            hideSelectedOptions={false}
            onChange={handleTemplateChange}
            loadOptions={debouncedLoadOptions}
            defaultOptions
            isMulti={false}
            placeholder="Type to search email templates..."
            isClearable={true}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            noOptionsMessage={({ inputValue }) =>
              inputValue
                ? `No templates found for "${inputValue}"`
                : "Start typing to search templates"
            }
          />
        </div>
        <div className="input-box">
          <label className="input-label" htmlFor="emailSubject">
            Email Subject
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter email subject"
            name="emailSubject"
            value={formData.emailSubject}
            onChange={(e) => handleInputChange("emailSubject", e.target.value)}
          />
        </div>
        <div className="input-box">
          <label className="input-label" htmlFor="emailBody">
            Email Body
          </label>
          <div className="email-editor-wrapper">
            <Editor
              editorState={editorState}
              onEditorStateChange={handleEditorStateChange}
              wrapperClassName="email-editor-wysiwyg-wrapper"
              toolbarClassName="email-editor-toolbar"
              editorClassName="email-editor-content"
              toolbar={{
                options: [
                  "inline",
                  // "blockType",
                  "list",
                  "link",
                  "image",
                ],
                inline: {
                  options: ["bold", "italic", "underline"],
                },
                // blockType: {
                //   inDropdown: true,
                //   options: ["Normal", "H1", "H2", "H3", "Blockquote"],
                // },
                link: {
                  options: ["link", "unlink"],
                },
              }}
            />
          </div>
        </div>
        <div className="input-box">
          <div className="email-toggle-buttons">
            <button
              className={`toggle-btn ${showFromFields ? "active" : ""}`}
              onClick={() => setShowFromFields(!showFromFields)}
            >
              From
            </button>
          </div>
        </div>
        {showFromFields && (
          <div style={{ display: "flex", gap: "12px" }}>
            <div className="input-box">
              <label className="input-label" htmlFor="fromName">
                From Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter From Name"
                name="fromName"
                value={formData.fromName}
                onChange={(e) => handleInputChange("fromName", e.target.value)}
              />
              {errors.fromName && (
                <div className="text-danger error-message">
                  {errors.fromName}
                </div>
              )}
            </div>
            <div className="input-box">
              <label className="input-label" htmlFor="fromEmail">
                From Email <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter From Email"
                name="fromEmail"
                value={formData.fromEmail}
                onChange={(e) => handleInputChange("fromEmail", e.target.value)}
              />
              {errors.fromEmail && (
                <div className="text-danger error-message">
                  {errors.fromEmail}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SendEmailToLeadModal;



// WEBPACK FOOTER //
// ./src/components/crm/leads/components/SendEmailToLeadModal.js